import { useState, useRef } from "react";
import { X, Camera, Loader2, Image as ImageIcon, MapPin } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { useGeolocation } from "@/hooks/useGeolocation";
import { toast } from "sonner";

export default function ReceiverModal({ open, onClose, onConfirm, deliveryCliente }) {
  const [name, setName] = useState("");
  const [evidenciaPath, setEvidenciaPath] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [gpsCaptured, setGpsCaptured] = useState(null);
  const fileInputRef = useRef(null);
  const { getPosition } = useGeolocation();

  if (!open) return null;

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Formato no permitido (JPG/PNG/WebP)");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Archivo demasiado grande (máx 8MB)");
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await axios.post(`${API}/uploads/evidencia`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEvidenciaPath(res.data.path);
      setPreviewUrl(URL.createObjectURL(file));
      toast.success("Foto cargada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir foto");
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setName("");
    setEvidenciaPath(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    let gps = gpsCaptured;
    if (!gps) {
      try {
        gps = await getPosition();
        setGpsCaptured(gps);
      } catch {
        // GPS optional — proceed without it but warn once
        toast.warning("Sin ubicación GPS — la entrega se registrará sin coordenadas");
      }
    }
    try {
      await onConfirm(name.trim(), evidenciaPath, gps);
      reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center bg-zinc-950/70 backdrop-blur-sm p-4"
      data-testid="receiver-modal"
    >
      <div className="bg-white border border-zinc-200 w-full max-w-md my-4 sm:my-8 flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)]">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200 flex-shrink-0 bg-white">
          <div className="min-w-0 flex-1 pr-3">
            <p className="eyebrow text-zinc-500">Confirmar entrega</p>
            <h2 className="font-display text-xl font-bold mt-1 truncate">{deliveryCliente}</h2>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-zinc-100 flex-shrink-0" data-testid="btn-close-receiver">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleConfirm} className="p-5 space-y-5 overflow-y-auto flex-1 min-h-0">
          <div>
            <label className="eyebrow text-zinc-600 mb-2 block">¿A quién entregaste?</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre de quien recibió"
              data-testid="input-recibido-por"
              autoFocus
              className="w-full px-4 py-3 border border-zinc-300 bg-white text-sm focus:outline-none focus:border-zinc-950"
            />
          </div>

          <div>
            <label className="eyebrow text-zinc-600 mb-2 block">Foto de evidencia (opcional)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              onChange={handleFileChange}
              data-testid="input-evidencia"
              className="hidden"
            />
            {!previewUrl ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="btn-upload-evidencia"
                className="w-full flex items-center justify-center gap-2 px-4 py-4 border border-dashed border-zinc-300 hover:border-zinc-950 hover:bg-zinc-50 transition-colors text-sm text-zinc-600"
              >
                {uploading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Subiendo...</>
                ) : (
                  <><Camera className="h-4 w-4" /> Tomar / Subir foto</>
                )}
              </button>
            ) : (
              <div className="relative border border-zinc-200 bg-zinc-50">
                <img src={previewUrl} alt="Evidencia" className="w-full h-48 object-cover" data-testid="evidencia-preview" />
                <button
                  type="button"
                  onClick={() => { setEvidenciaPath(null); URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
                  className="absolute top-2 right-2 bg-white border border-zinc-200 p-1.5 hover:bg-zinc-100"
                  data-testid="btn-remove-evidencia"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-2 py-1 flex items-center gap-1">
                  <ImageIcon className="h-3 w-3" /> Foto cargada
                </div>
              </div>
            )}
          </div>

          <div className="text-xs flex items-center gap-2 px-3 py-2 border border-ekin-100 bg-ekin-50 text-ekin-700">
            <MapPin className="h-3.5 w-3.5" />
            <span>Al confirmar se capturará tu ubicación GPS como punto de entrega.</span>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="btn-tactile flex-1 py-3 border border-zinc-300 text-sm font-medium hover:bg-zinc-50"
              data-testid="btn-cancel-receiver"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!name.trim() || submitting || uploading}
              data-testid="btn-confirm-receiver"
              className="btn-tactile flex-[2] py-3 bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800 disabled:opacity-50"
            >
              {submitting ? "Confirmando..." : "Confirmar entrega"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
